import React, { useState, useRef } from "react";
import Axios from 'axios';
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";

function Signup(props) {
  const emailRef = useRef(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [strength, setStrength] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  let navigate = useNavigate();

  const isEmail = (email) =>
    /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email);

  const checkPasswordStrength = (pwd) => {
    const criteria = [
      /.{8,}/,
      /[A-Z]/,
      /[a-z]/,
      /\d/,
      /[@$!%*?&]/,
    ];
    const passed = criteria.filter((r) => r.test(pwd)).length;
    if (pwd.length === 0) return "";
    if (passed <= 2) return "Weak";
    if (passed <= 4) return "Moderate";
    return "Strong";
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setStrength(checkPasswordStrength(e.target.value));
  };

  const strengthColor = {
    Weak: "text-red-400",
    Moderate: "text-yellow-400",
    Strong: "text-green-400",
  };

  const register = (e) => {
    e.preventDefault();
    const errs = {};

    if (!isEmail(username)) {
      emailRef.current.focus();
      errs.email = "Enter a valid email.";
      setErrors(errs);
      return;
    }

    setErrors({});

    Axios.post("https://mygamename.com/php/signup.php", {
      username,
      password,
    }).then((response) => {
      if (response.data.message === "EAU") {
        setErrors({ email: "Email already in use." });
        return;
      }

      Axios.post("https://mygamename.com/php/login.php", {
        username,
        password,
      }).then((response) => {
        if (response.data.message) {
          setErrors({ email: response.data.message });
        } else {
          let email, token;
          if (Array.isArray(response.data)) {
            email = response.data[0].email;
            token = response.data[0].token;
          } else if (typeof response.data === 'string') {
            const candidate = response.data.trim();
            if (/^[\w-]+\.[\w-]+\.[\w-]+$/.test(candidate)) {
              token = candidate;
              try {
                const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
                email = payload.email;
              } catch {}
            }
          }
          if (token) {
            localStorage.setItem('token', token);
            props.setLoginStatus(true);
            props.setUEmail(email);
            navigate('/create');
          } else {
            setErrors({ email: 'Signup failed. Please try again.' });
          }
        }
      });
    });
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-[#1a1a1a] border border-[#222222] rounded-xl p-8">
        <h1 className="text-3xl font-extrabold text-[#F5F5F5] mb-2 tracking-tight">Create account</h1>
        <p className="text-[#F5F5F5]/50 text-sm mb-8">Join us and start saving your custom game names.</p>

        <form onSubmit={register} className="flex flex-col gap-4">
          <div>
            <input
              ref={emailRef}
              type="text"
              placeholder="Email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="bg-[#111111] border border-[#333333] text-[#F5F5F5] text-sm rounded-lg focus:ring-[#8B5CF6] focus:border-[#8B5CF6] block w-full p-3 placeholder-[#F5F5F5]/30 outline-none transition-colors"
            />
          </div>
          <div>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={handlePasswordChange}
                className="bg-[#111111] border border-[#333333] text-[#F5F5F5] text-sm rounded-lg focus:ring-[#8B5CF6] focus:border-[#8B5CF6] block w-full p-3 pr-10 placeholder-[#F5F5F5]/30 outline-none transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#F5F5F5]/30 hover:text-[#F5F5F5]/70 transition-colors"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {strength && (
              <p className={`text-xs font-semibold mt-1 ${strengthColor[strength]}`}>{strength}</p>
            )}
          </div>

          {Object.entries(errors).map(([key, error]) => (
            <span key={key} className="text-red-400 text-xs font-semibold">{error}</span>
          ))}

          <button
            type="submit"
            className="w-full bg-[#8B5CF6] hover:bg-[#7C3AED] text-[#F5F5F5] font-semibold py-3 rounded-lg transition-colors mt-2"
          >
            Sign Up
          </button>
        </form>

        <p className="text-center text-[#F5F5F5]/50 text-sm mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-[#8B5CF6] hover:text-[#7C3AED] font-semibold transition-colors">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

export default Signup;
